import React, { useEffect, useState } from "react"
import { get, post } from "../../utils/apiClient"
import toast from "react-hot-toast"
import { Settings, UserCog, Info } from "lucide-react"
import Select from "react-select"

const SETTING_LABEL = {
  SEND_OUTBOUND_FAX_NOTIFICATION_EMAIL: {
    label: "Outbound Fax Email Notifications",
    description:
      "Enable to receive email notifications for all outbound faxes.",
  },
  // SEND_INBOUND_FAX_NOTIFICATION_EMAIL: {
  //   label: "Inbound Fax Notifications",
  //   description: "Enable alerts for incoming documents",
  // },
  OUTBOUND_FAX_ONLY: {
    label: "Outbound Fax Only Access",
    description:
      "Enable to restrict the user to outbound fax operations only. Inbound fax access will not be available.",
  },
  // INBOUND_FAX_ONLY: {
  //   label: "Inbound Fax Only",
  //   description: "Restrict this user to inbound operations only",
  // },
}

const UserSettings = ({ authUser }) => {
  const defaultUser = {
    value: authUser.emailAddress,
    label: "Select a User...",
  }

  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState(null)
  const [personalSettings, setPersonalSettings] = useState({})
  const [globalSettings, setGlobalSettings] = useState({})
  const [loadingSettings, setLoadingSettings] = useState(false)

  const [meta, setMeta] = useState({
    personal: {},
    global: {},
  })

  const [loading, setLoading] = useState(false)
  const [personalSaving, setPersonalSaving] = useState(false)
  const [globalSaving, setGlobalSaving] = useState(false)

  // ---------- HELPERS ----------
  const mapSettingsArrayToObject = (arr = []) => {
    const obj = {}
    arr.forEach((item) => {
      obj[item.key] = item.value === "true"
    })
    return obj
  }

  const mergeSettingLabels = (data) => {
    const merge = (settings = []) => {
      return settings.map((item) => {
        const labelData = SETTING_LABEL[item.key] || {}

        return {
          ...item,
          label: labelData.label || item.key,
          description: labelData.description || "",
        }
      })
    }

    return {
      ...data,
      personalSettings: {
        ...data.personalSettings,
        settings: merge(data.personalSettings?.settings),
      },
      globalSettings: {
        ...data.globalSettings,
        settings: merge(data.globalSettings?.settings),
      },
    }
  }

  const mapMeta = (arr = []) => {
    return arr.reduce((acc, cur) => {
      acc[cur.key] = {
        label: cur.label,
        description: cur.description,
      }
      return acc
    }, {})
  }

  // ---------- FETCH SETTINGS ----------
  const fetchSettings = async (targetUser = null) => {
    setLoadingSettings(true)
    try {
      let url = "/manage/settings"

      if (targetUser) {
        url += `?targetUserEmail=${targetUser.emailAddress}&targetUserNetId=${targetUser.netId}`
      }

      const data = await get(url)

      const mergedData = mergeSettingLabels(data)

      setPersonalSettings(
        mapSettingsArrayToObject(mergedData?.personalSettings?.settings),
      )

      setGlobalSettings(
        mapSettingsArrayToObject(mergedData?.globalSettings?.settings),
      )

      setMeta({
        personal: mapMeta(mergedData?.personalSettings?.settings),
        global: mapMeta(mergedData?.globalSettings?.settings),
      })
      setLoadingSettings(false)
    } catch (err) {
      console.error(err)
      toast.error("Failed to load settings")
      setLoadingSettings(false)
    }
  }

  // ---------- FETCH USERS ----------
  const fetchUsers = async () => {
    try {
      const data = await get("/admin/netId-fax-number-associations")
      setUsers(data || [])
    } catch (err) {
      console.error(err)
      toast.error("Failed to load users")
    }
  }

  // ---------- INIT ----------
  useEffect(() => {
    setLoading(true)
    Promise.all([fetchSettings(), fetchUsers()]).finally(() =>
      setLoading(false),
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---------- HANDLERS ----------
  const togglePersonal = (key) => {
    setPersonalSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const toggleGlobal = (key) => {
    setGlobalSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const handleUserChange = (value) => {
    const user = users.find((u) => u.emailAddress === value)

    setSelectedUser(user || null)

    if (user) {
      fetchSettings(user)
    } else {
      fetchSettings()
    }
  }

  // ---------- SAVE ----------
  const handleSavePersonalSettings = async () => {
    const params = {
      settings: Object.entries(personalSettings).map(([key, val]) => ({
        key,
        value: String(val),
      })),
    }

    setPersonalSaving(true)
    try {
      await post("/manage/settings/personal", params)
      toast.success("Personal settings saved")
    } catch (err) {
      console.error(err)
      toast.error("Failed to save personal settings")
    } finally {
      setPersonalSaving(false)
    }
  }

  const handleSaveGlobalSettings = async () => {
    const params = {
      targetUserEmail: selectedUser?.emailAddress || authUser?.emailAddress,
      targetUserNetId: selectedUser?.netId || authUser?.netId,
      settings: Object.entries(globalSettings).map(([key, val]) => ({
        key,
        value: String(val),
      })),
    }

    setGlobalSaving(true)
    try {
      await post("/manage/settings/global", params)
      toast.success("Global settings saved")
    } catch (err) {
      console.error(err)
      toast.error("Failed to save global settings")
    } finally {
      setGlobalSaving(false)
    }
  }

  const optionUsers = [
    defaultUser,
    ...users.map((n) => ({
      value: n.emailAddress,
      label: n.emailAddress,
    })),
  ]

  // ---------- UI ----------
  return (
    <div style={{ padding: "20px" }}>
      {loading ? (
        <div className="loading-container-fax-list">
          <div className="loading-message">Loading...</div>
        </div>
      ) : (
        <>
          <div
            style={{
              display: "flex",
              gap: "10px",
              marginTop: "10px",
              flexDirection: "column",
            }}
          >
            {/* PERSONAL SETTINGS */}
            <div
              className="permission-box"
              style={{ flex: 1, maxWidth: "400px", padding: "30px" }}
            >
              <h3 style={{ display: "flex" }}>
                <UserCog
                  size={16}
                  color="black"
                  style={{ marginRight: "4px" }}
                />
                My Personal Settings
              </h3>

              {Object.entries(meta.personal).map(([key, item]) => {
                return (
                  <div key={key} style={{ display: "flex" }}>
                    <label style={{ display: "flex", alignItems: "center" }}>
                      <input
                        type="checkbox"
                        checked={personalSettings[key] || false}
                        onChange={() => togglePersonal(key)}
                      />
                      {item.label}
                      <span
                        title={`${item.description}`}
                        style={{
                          marginLeft: "5px",
                          cursor: "default",
                        }}
                      >
                        <Info size={14} color="#E89518" />
                      </span>
                    </label>
                  </div>
                )
              })}

              <button
                className="faxes-button"
                onClick={handleSavePersonalSettings}
                style={{ marginTop: "25px", marginLeft: "0px" }}
              >
                {!personalSaving ? "Save" : "Saving ..."}
              </button>
            </div>

            {/* GLOBAL SETTINGS */}
            {authUser.loggedInUserType === "IT" && (
              <div
                className="permission-box"
                style={{ flex: 1, maxWidth: "400px", padding: "30px" }}
              >
                <h3 style={{ display: "flex" }}>
                  <Settings
                    size={16}
                    color="black"
                    style={{ marginRight: "4px" }}
                  />
                  Global User Settings
                </h3>

                <Select
                  id="roleSelect"
                  className="basic-single"
                  classNamePrefix="select"
                  value={optionUsers.find(
                    (option) => option.value === selectedUser,
                  )}
                  onChange={(selectedOption) =>
                    handleUserChange(selectedOption.value)
                  }
                  options={optionUsers}
                  styles={{
                    control: (base) => ({
                      ...base,
                      borderRadius: 0,
                      maxWidth: "500px",
                      marginBottom: "10px",
                    }),
                  }}
                  placeholder="Select User..."
                />

                {loadingSettings ? (
                  <>Loading...</>
                ) : (
                  <div className={selectedUser ? "" : "disabled"}>
                    {Object.entries(meta.global).map(([key, item]) => (
                      <div key={key} style={{ display: "flex" }}>
                        <label
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <input
                            type="checkbox"
                            checked={globalSettings[key] || false}
                            onChange={() => toggleGlobal(key)}
                          />
                          {item.label}
                        </label>
                        <span
                          title={`${item.description}`}
                          style={{
                            marginLeft: "5px",
                            cursor: "default",
                          }}
                        >
                          <Info size={14} color="#E89518" />
                        </span>
                      </div>
                    ))}

                    <button
                      className="faxes-button"
                      onClick={handleSaveGlobalSettings}
                      style={{ marginTop: "25px", marginLeft: "0px" }}
                    >
                      {!globalSaving ? "Save" : "Saving ..."}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default UserSettings
